import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { Check, ChevronsUpDown, Phone, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/combobox";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneNumberDisplay } from "@/components/ui/phone-number-display";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/components/ui/use-toast";
import { BackendPicker } from "@/components/shared/BackendPicker";
import { cn } from "@/lib/utils";
import { apiServerHeaders } from "@/lib/apiServerHeaders";
import { useBackendFlags } from "@/hooks/useBackendFlags";
import { useDebounce } from "@/hooks/useDebounce";
import { leadService } from "@/services/leadService";
import outboundCallService from "@/services/outboundCallService";
import type { OutboundCallBackend } from "@/services/outboundCallService";
import { useAuth } from "../../../supabase/auth";
import { supabase } from "../../../supabase/supabase";
import { useOrganization } from "../../App";
import { CallErrorDialog } from "./CallErrorDialog";

export interface CallableLead {
  id: string;
  first_name: string;
  last_name: string;
  email?: string | null;
  phone_number: string;
}

interface AssistantOption {
  id: string;
  name: string;
  variables?: string[];
}

interface InitiateCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Lead is fixed (opened from a lead row): the dialog shows an assistant picker. */
  lead?: CallableLead | null;
  /** Assistant is fixed (opened from an assistant): the dialog shows a lead picker. */
  assistantId?: string | null;
  /** Video interview flow; needs a fixed `lead`. */
  video?: boolean;
}

const LAST_ASSISTANT_KEY = "voxi_last_assistant";

const leadFullName = (lead: CallableLead) =>
  `${lead.first_name || ""} ${lead.last_name || ""}`.trim();

export function InitiateCallDialog({
  open,
  onOpenChange,
  lead: fixedLead = null,
  assistantId: fixedAssistantId = null,
  video: isVideoCall = false,
}: InitiateCallDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { currentOrganization } = useOrganization();
  // Call backends this organization has (V1 voice, V2 voice, V1 video)
  const { defaultBackend, voiceV2 } = useBackendFlags();

  const assistantFixed = !!fixedAssistantId;

  // Lead: fixed by the caller, or picked here when the assistant is fixed
  const [pickedLead, setPickedLead] = useState<CallableLead | null>(null);
  const leadToCall = fixedLead ?? pickedLead;
  const [leadSearch, setLeadSearch] = useState("");
  const debouncedLeadSearch = useDebounce(leadSearch, 300);
  const [leadResults, setLeadResults] = useState<CallableLead[]>([]);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null);
  const [shouldRestoreLastAssistant, setShouldRestoreLastAssistant] = useState(false);
  const [assistants, setAssistants] = useState<AssistantOption[]>([]);
  const [loadingAssistants, setLoadingAssistants] = useState(false);
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [initiatingCall, setInitiatingCall] = useState(false);
  const [selectedCallBackend, setSelectedCallBackend] =
    useState<OutboundCallBackend>("v1");

  // Searchable "Call Method" picker
  const [callMethodOpen, setCallMethodOpen] = useState(false);
  // The pickers portal into the dialog so the dialog's focus trap lets their search inputs be typed in
  const [callDialogEl, setCallDialogEl] = useState<HTMLDivElement | null>(null);

  // Video call dialog steps
  const [dialogStep, setDialogStep] = useState<"assistant" | "monitoring" | "link">("assistant");
  const [tabChangeDetection, setTabChangeDetection] = useState(false);
  const [externalMonitorDetection, setExternalMonitorDetection] = useState(false);
  const [enableScreenSharing, setEnableScreenSharing] = useState(false);
  const [enableIntermittentFaceOutOfFocus, setEnableIntermittentFaceOutOfFocus] = useState(false);
  const [generatedVideoLink, setGeneratedVideoLink] = useState("");
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [startTime, setStartTime] = useState("");

  // Phone number selection
  const [selectedPhoneNumberId, setSelectedPhoneNumberId] = useState<string | null>(null);
  const [phoneNumbers, setPhoneNumbers] = useState<
    Array<{ id: string; phone_number: string; is_default?: boolean }>
  >([]);
  const [loadingPhoneNumbers, setLoadingPhoneNumbers] = useState(false);

  const [isCallErrorDialogOpen, setIsCallErrorDialogOpen] = useState(false);
  const [callErrorMessage, setCallErrorMessage] = useState("");

  // Last selected assistant is remembered per organization
  const getLastAssistantId = () => {
    const orgId = currentOrganization?.id;
    if (!orgId) return null;
    try {
      const stored = localStorage.getItem(`${LAST_ASSISTANT_KEY}_${orgId}`);
      if (stored) {
        const { assistantId } = JSON.parse(stored);
        return assistantId;
      }
    } catch {
      // Ignore parse errors
    }
    return null;
  };

  const saveLastAssistantId = (assistantId: string | null) => {
    const orgId = currentOrganization?.id;
    if (!orgId || !assistantId) return;
    localStorage.setItem(
      `${LAST_ASSISTANT_KEY}_${orgId}`,
      JSON.stringify({ assistantId, timestamp: Date.now() })
    );
  };

  const fetchAssistants = async () => {
    setLoadingAssistants(true);
    try {
      const { data, error } = await supabase
        .from("assistants")
        .select("id, name, variables")
        .order("name");

      if (error) throw error;
      setAssistants(data || []);
    } catch (error) {
      Sentry.logger.error("Error fetching assistants", {
        error,
        log_source: "initiate_call_dialog",
        organization_id: currentOrganization?.id,
      });
      toast({
        title: "Error",
        description: "Failed to load assistants. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingAssistants(false);
    }
  };

  // Fetch outbound phone numbers for the dropdown
  const fetchPhoneNumbers = async () => {
    setLoadingPhoneNumbers(true);
    try {
      const { data, error } = await supabase
        .from("phone_numbers")
        .select("id, phone_number, is_default")
        .eq("type", "outbound")
        .order("is_default", { ascending: false }) // Default numbers first
        .order("phone_number");

      if (error) throw error;

      const phoneNumbersData = data || [];
      setPhoneNumbers(phoneNumbersData);

      const defaultNumber = phoneNumbersData.find((pn) => pn.is_default === true);
      if (defaultNumber) {
        setSelectedPhoneNumberId(defaultNumber.id);
      } else if (phoneNumbersData.length > 0) {
        setSelectedPhoneNumberId(phoneNumbersData[0].id);
      }
    } catch (error) {
      Sentry.logger.error("Error fetching phone numbers", {
        error,
        log_source: "initiate_call_dialog",
        organization_id: currentOrganization?.id,
      });
      toast({
        title: "Error",
        description: "Failed to load outbound phone numbers. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoadingPhoneNumbers(false);
    }
  };

  // Reset and load data each time the dialog opens
  useEffect(() => {
    if (!open) return;
    setPickedLead(null);
    setLeadSearch("");
    setLeadResults([]);
    // Video interviews run on V1; a voice call starts on the organization's default backend
    setSelectedCallBackend(isVideoCall ? "v1" : voiceV2 ? "v2" : defaultBackend);
    setDialogStep("assistant");
    setTabChangeDetection(false);
    setExternalMonitorDetection(false);
    setEnableScreenSharing(false);
    setEnableIntermittentFaceOutOfFocus(false);
    setGeneratedVideoLink("");
    setStartTime("");
    // A fixed assistant is selected up front; otherwise restore the last one once the list loads
    setSelectedAssistantId(fixedAssistantId);
    setShouldRestoreLastAssistant(!fixedAssistantId);
    setVariableValues({});
    fetchAssistants();
    fetchPhoneNumbers();
  }, [open]);

  // Search leads on the server when the assistant is fixed and a lead is to be picked
  useEffect(() => {
    if (!open || !assistantFixed || !currentOrganization?.id) return;
    let cancelled = false;
    setLoadingLeads(true);
    leadService
      .getLeadsWithPagination({
        organizationId: currentOrganization.id,
        limit: 20,
        method: "cursor",
        searchQuery: debouncedLeadSearch.trim() || undefined,
      })
      .then((res) => {
        if (!cancelled) setLeadResults(res.data as CallableLead[]);
      })
      .catch((error) => {
        Sentry.logger.error("Error searching leads", {
          error,
          log_source: "initiate_call_dialog",
          organization_id: currentOrganization.id,
        });
      })
      .finally(() => {
        if (!cancelled) setLoadingLeads(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, assistantFixed, debouncedLeadSearch, currentOrganization?.id]);

  // Restore the last selected assistant once the assistant list has loaded
  useEffect(() => {
    if (!loadingAssistants && shouldRestoreLastAssistant && assistants.length > 0) {
      const lastAssistantId = getLastAssistantId();
      if (lastAssistantId && assistants.some((a) => a.id === lastAssistantId)) {
        setSelectedAssistantId(lastAssistantId);
        if (leadToCall) initializeVariableValues(leadToCall, lastAssistantId);
      }
      setShouldRestoreLastAssistant(false);
    }
  }, [loadingAssistants, shouldRestoreLastAssistant, assistants]);

  // With a fixed assistant, fill the variables once the lead is picked and the assistant is loaded
  useEffect(() => {
    if (assistantFixed && leadToCall && assistants.length > 0) {
      initializeVariableValues(leadToCall, fixedAssistantId);
    }
  }, [assistantFixed, leadToCall?.id, assistants]);

  const getSelectedAssistantVariables = () => {
    if (!selectedAssistantId) return [];
    const assistant = assistants.find((a) => a.id === selectedAssistantId);
    return assistant?.variables || [];
  };

  // Initialize variable values from a lead for the given assistant
  const initializeVariableValues = (lead: CallableLead, assistantId?: string | null) => {
    try {
      const assistant = assistants.find((a) => a.id === (assistantId ?? selectedAssistantId));

      const variables = assistant?.variables || [];
      const initialValues: Record<string, string> = {};

      if (!variables || variables.length === 0) {
        setVariableValues({});
        return;
      }

      variables.forEach((variable) => {
        if (!variable) return;

        const variableName = String(variable);

        // Map variable names to lead properties
        try {
          switch (variableName.toLowerCase()) {
            case "first_name":
              initialValues[variableName] = lead?.first_name || "";
              break;
            case "last_name":
              initialValues[variableName] = lead?.last_name || "";
              break;
            case "name":
            case "lead_contact_name":
              initialValues[variableName] = lead ? leadFullName(lead) : "";
              break;
            case "email":
              initialValues[variableName] = lead?.email || "";
              break;
            case "phone":
            case "phone_number":
            case "lead_contact_number":
              initialValues[variableName] = lead?.phone_number || "";
              break;
            default:
              initialValues[variableName] = "";
          }
        } catch (err) {
          Sentry.logger.error("Error mapping variable", {
            error: err,
            log_source: "initiate_call_dialog",
            variable_name: variableName,
            lead_id: lead?.id,
          });
          initialValues[variableName] = "";
        }
      });

      setVariableValues(initialValues);
    } catch (error) {
      Sentry.logger.error("Error initializing variable values", {
        error,
        log_source: "initiate_call_dialog",
        lead_id: lead?.id,
        assistant_id: assistantId || selectedAssistantId,
      });
      setVariableValues({});
    }
  };

  const handleCallMethodChange = (newAssistantId: string | null) => {
    setSelectedAssistantId(newAssistantId);
    saveLastAssistantId(newAssistantId);

    if (newAssistantId && leadToCall) {
      initializeVariableValues(leadToCall, newAssistantId);
    } else {
      setVariableValues({});
    }
  };

  const generateVideoCallLink = async () => {
    if (!leadToCall || !selectedAssistantId) return;

    setIsGeneratingLink(true);
    try {
      // Call the backend API to generate a short link
      const CALL_SERVICE_URL = import.meta.env.VITE_CALL_SERVICE_URL || "http://localhost:8000";
      const response = await fetch(`${CALL_SERVICE_URL}/api/video-link`, {
        method: "POST",
        headers: await apiServerHeaders(),
        body: JSON.stringify({
          lead_id: leadToCall.id,
          agent_id: selectedAssistantId,
          proctoring_tools: {
            tab_change_detection: tabChangeDetection,
            screen_share: enableScreenSharing,
            multiple_monitor_detection: externalMonitorDetection,
            face_out_of_view: enableIntermittentFaceOutOfFocus,
            face_blur_detection: enableIntermittentFaceOutOfFocus,
          },
          organization_id: currentOrganization?.id,
          start_time: startTime ? new Date(startTime).toISOString() : null,
          echo_cancellation: true,
          metadata: {
            variables: variableValues,
            generated_by: user?.id,
            generated_at: new Date().toISOString(),
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate video link");
      }

      const data = await response.json();
      // Construct the full URL using the current domain and the token
      const fullVideoCallUrl = `${window.location.origin}/video-call/${data.token}`;
      setGeneratedVideoLink(fullVideoCallUrl);
      setDialogStep("link");

      toast({
        title: "Success",
        description: "Video interview link generated successfully!",
      });
    } catch (error) {
      Sentry.logger.error("Error generating video link", {
        error,
        log_source: "initiate_call_dialog",
        lead_id: leadToCall?.id,
        assistant_id: selectedAssistantId,
        organization_id: currentOrganization?.id,
      });
      toast({
        title: "Error",
        description: "Failed to generate video link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCall = async () => {
    if (!leadToCall) return;

    // Manual phone call option
    if (!selectedAssistantId) {
      window.open(`tel:${leadToCall.phone_number}`, "_blank");
      toast({
        title: "Opening Phone Dialer",
        description: `Calling ${leadFullName(leadToCall)}...`,
      });
      onOpenChange(false);
      return;
    }

    // If it's a video call and we're in the link step, generate the link
    if (isVideoCall && dialogStep === "link") {
      try {
        await generateVideoCallLink();
        return;
      } catch (error) {
        toast({
          title: "Error",
          description: "Failed to generate video call link.",
          variant: "destructive",
        });
        return;
      }
    }

    // If it's a video call, navigate to video call page with parameters
    if (isVideoCall) {
      const params = new URLSearchParams({
        assistantId: selectedAssistantId,
      });

      if (Object.keys(variableValues).length > 0) {
        params.append("variables", JSON.stringify(variableValues));
      }

      params.append("tabChangeDetection", tabChangeDetection.toString());
      params.append("externalMonitorDetection", externalMonitorDetection.toString());
      params.append("screenSharing", enableScreenSharing.toString());
      params.append("blurDetection", enableIntermittentFaceOutOfFocus.toString());

      navigate(`/video-call/${leadToCall.id}?${params.toString()}`);
      onOpenChange(false);
      return;
    }

    // Check if there's a phone number selected for outbound calls
    if (selectedCallBackend === "v1" && !selectedPhoneNumberId) {
      toast({
        title: "Phone Number Required",
        description: "Please select an outbound phone number to use for this call.",
        variant: "destructive",
      });
      return;
    }

    if (!currentOrganization) {
      toast({
        title: "Organization Required",
        description: "Organization information is missing. Please refresh the page.",
        variant: "destructive",
      });
      return;
    }

    // Automated call with AI agent
    setInitiatingCall(true);
    try {
      const callResult = await outboundCallService.initiateCallToExistingLead(
        leadToCall.id,
        selectedAssistantId,
        selectedPhoneNumberId,
        currentOrganization.id,
        variableValues,
        undefined, // campaignId
        isVideoCall,
        { backend: selectedCallBackend, startLanguage: "" }
      );

      if (callResult && callResult.success) {
        toast({
          title: "Call Initiated",
          description: `AI assistant is calling ${leadFullName(leadToCall)}...`,
        });
      } else {
        toast({
          title: "Call Setup Required",
          description:
            (callResult && callResult.message) || "Call service not fully configured",
          variant: "destructive",
        });
        if (callResult && callResult.instructions) {
          Sentry.logger.info("Call setup instructions received", {
            log_source: "initiate_call_dialog",
            instructions: callResult.instructions,
          });
        }
      }
    } catch (error) {
      Sentry.logger.error("Error initiating call", {
        error,
        log_source: "initiate_call_dialog",
        lead_id: leadToCall.id,
        assistant_id: selectedAssistantId,
        phone_number_id: selectedPhoneNumberId,
        is_video_call: isVideoCall,
      });

      let errorMessage = "Failed to initiate the call. Please try again.";
      if (error instanceof Error && error.message) {
        errorMessage = error.message;
      }

      // Show detailed error dialog for prerequisite errors, toast for others
      if (errorMessage.includes("Missing requirements:")) {
        setCallErrorMessage(errorMessage);
        setIsCallErrorDialogOpen(true);
      } else {
        toast({
          title: "Call Failed",
          description: errorMessage,
          variant: "destructive",
          duration: 8000, // Show longer for detailed error messages
        });
      }
    } finally {
      setInitiatingCall(false);
      onOpenChange(false);
    }
  };

  const leadOptions = (() => {
    const options = leadResults.map((l) => ({
      value: l.id,
      label: `${leadFullName(l)} - ${l.phone_number}`,
      keywords: l.email || undefined,
    }));
    // Keep the picked lead listed even when the current search no longer returns it
    if (pickedLead && !options.some((o) => o.value === pickedLead.id)) {
      options.unshift({
        value: pickedLead.id,
        label: `${leadFullName(pickedLead)} - ${pickedLead.phone_number}`,
        keywords: pickedLead.email || undefined,
      });
    }
    return options;
  })();

  const fixedAssistantName = assistants.find((a) => a.id === fixedAssistantId)?.name;

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next);
          if (!next && isVideoCall) {
            // Reset dialog state when closing
            setDialogStep("assistant");
            setTabChangeDetection(false);
            setExternalMonitorDetection(false);
            setEnableScreenSharing(false);
            setEnableIntermittentFaceOutOfFocus(false);
            setGeneratedVideoLink("");
            setStartTime("");
          }
        }}
      >
        <DialogContent ref={setCallDialogEl} className="w-[90vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isVideoCall
                ? dialogStep === "assistant"
                  ? "Select Assistant for Video Call"
                  : dialogStep === "monitoring"
                    ? "Monitoring Options"
                    : "Video Call Link"
                : "Initiate Call"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {assistantFixed ? (
              <div className="space-y-2">
                <Label htmlFor="lead">Lead</Label>
                <Combobox
                  id="lead"
                  value={pickedLead?.id ?? ""}
                  onValueChange={(id) => {
                    const lead = leadResults.find((l) => l.id === id);
                    if (lead) setPickedLead(lead);
                  }}
                  options={leadOptions}
                  placeholder="Select a lead"
                  searchPlaceholder="Search by name, email or phone..."
                  emptyText="No lead found."
                  onSearchChange={setLeadSearch}
                  loading={loadingLeads}
                  container={callDialogEl}
                  disabled={initiatingCall}
                />
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                {isVideoCall ? (
                  <Video className="h-5 w-5 text-primary" />
                ) : (
                  <Phone className="h-5 w-5 text-primary" />
                )}
                <span className="font-medium flex items-center gap-2">
                  {leadToCall && leadFullName(leadToCall)} -
                  {leadToCall?.phone_number && (
                    <PhoneNumberDisplay phoneNumber={leadToCall.phone_number} />
                  )}
                </span>
              </div>
            )}

            {isVideoCall && dialogStep === "link" ? (
              // Link generation step
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Share this link with {leadToCall && leadFullName(leadToCall)} to start the
                  video interview.
                </p>
                <div className="flex gap-2">
                  <Input
                    value={generatedVideoLink}
                    readOnly
                    className="flex-1"
                    placeholder="Click 'Generate Link' to create the video call link"
                  />
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (generatedVideoLink) {
                        navigator.clipboard.writeText(generatedVideoLink);
                        toast({
                          title: "Copied!",
                          description: "Video call link copied to clipboard",
                        });
                      }
                    }}
                    disabled={!generatedVideoLink}
                  >
                    Copy
                  </Button>
                </div>
                {generatedVideoLink && (
                  <div className="text-sm text-muted-foreground space-y-2">
                    <p>This link includes:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>Selected AI assistant configuration</li>
                      <li>Pre-filled template variables</li>
                      <li>Monitoring settings</li>
                    </ul>
                  </div>
                )}
              </div>
            ) : isVideoCall && dialogStep === "monitoring" ? (
              // Monitoring options for video calls
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="tab-change">Tab Change Detection</Label>
                      <p className="text-sm text-muted-foreground">
                        Detect when the user switches browser tabs during the call
                      </p>
                    </div>
                    <Switch
                      id="tab-change"
                      checked={tabChangeDetection}
                      onCheckedChange={setTabChangeDetection}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="external-monitor">External Monitor Detection</Label>
                      <p className="text-sm text-muted-foreground">
                        Detect and report external monitors connected to the system
                      </p>
                    </div>
                    <Switch
                      id="external-monitor"
                      checked={externalMonitorDetection}
                      onCheckedChange={setExternalMonitorDetection}
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="space-y-1">
                      <Label htmlFor="screen-sharing">Enable Screen Sharing</Label>
                      <p className="text-sm text-muted-foreground">
                        Require continuous screen sharing throughout the session
                      </p>
                    </div>
                    <Switch
                      id="screen-sharing"
                      checked={enableScreenSharing}
                      onCheckedChange={setEnableScreenSharing}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="start-time">Start Time (Optional)</Label>
                    <Input
                      id="start-time"
                      type="datetime-local"
                      value={startTime}
                      onChange={(e) => setStartTime(e.target.value)}
                      placeholder="When should the link become active?"
                    />
                    <p className="text-sm text-muted-foreground">
                      If set, the video link will only be accessible from this time. Leave empty
                      for immediate access.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              // Assistant selection
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="assistant">Call Method</Label>
                  <div className="grid grid-cols-1 gap-2">
                    {assistantFixed ? (
                      <Input
                        id="assistant"
                        value={loadingAssistants ? "Loading assistant..." : fixedAssistantName ?? ""}
                        readOnly
                        disabled
                      />
                    ) : (
                      <Popover open={callMethodOpen} onOpenChange={setCallMethodOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            id="assistant"
                            variant="outline"
                            role="combobox"
                            aria-expanded={callMethodOpen}
                            className="w-full justify-between font-normal"
                            disabled={loadingAssistants || initiatingCall}
                          >
                            <span className="truncate">
                              {loadingAssistants
                                ? "Loading assistants..."
                                : assistants.find((a) => a.id === selectedAssistantId)?.name ||
                                  "Manual Phone Call"}
                            </span>
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent
                          container={callDialogEl}
                          className="w-(--radix-popover-trigger-width) p-0"
                          align="start"
                        >
                          <Command>
                            <CommandInput placeholder="Search assistants..." />
                            <CommandList>
                              <CommandEmpty>No assistant found.</CommandEmpty>
                              <CommandGroup>
                                <CommandItem
                                  value="Manual Phone Call"
                                  onSelect={() => {
                                    handleCallMethodChange(null);
                                    setCallMethodOpen(false);
                                  }}
                                >
                                  <Check
                                    className={cn(
                                      "mr-2 h-4 w-4",
                                      !selectedAssistantId ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                  Manual Phone Call
                                </CommandItem>
                              </CommandGroup>
                              <CommandGroup heading="AI Assistants">
                                {assistants.map((assistant) => (
                                  <CommandItem
                                    key={assistant.id}
                                    // cmdk filters on value; the id keeps duplicate names distinct
                                    value={`${assistant.name} ${assistant.id}`}
                                    onSelect={() => {
                                      handleCallMethodChange(assistant.id);
                                      setCallMethodOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4 shrink-0",
                                        selectedAssistantId === assistant.id
                                          ? "opacity-100"
                                          : "opacity-0"
                                      )}
                                    />
                                    <span className="truncate">{assistant.name}</span>
                                    {assistant.variables && assistant.variables.length > 0 && (
                                      <span className="ml-auto pl-2 text-xs text-muted-foreground shrink-0">
                                        {assistant.variables.length} variables
                                      </span>
                                    )}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>

                  {selectedAssistantId && (
                    <div className="text-sm text-muted-foreground mt-2">
                      <p>
                        An AI agent will call the lead and handle the conversation according to
                        the assistant's configuration.
                        {assistantFixed && " The saved configuration is used, not unsaved edits."}
                      </p>
                    </div>
                  )}

                  {!selectedAssistantId && (
                    <div className="text-sm text-muted-foreground mt-2">
                      <p>Your default phone app will open to make a manual call.</p>
                    </div>
                  )}

                  {/* Display variables if the selected assistant has any */}
                  {selectedAssistantId && getSelectedAssistantVariables().length > 0 && (
                    <div className="mt-4 border border-border rounded-md p-3">
                      <Label className="mb-2 block">Template Variables</Label>
                      <div className="grid grid-cols-3 gap-4">
                        {getSelectedAssistantVariables().map((variable) => (
                          <div key={variable} className="space-y-1">
                            <Label
                              htmlFor={`var-${variable}`}
                              className="text-xs text-muted-foreground"
                            >
                              {variable}
                            </Label>
                            <Input
                              id={`var-${variable}`}
                              value={variableValues[variable] || ""}
                              onChange={(e) =>
                                setVariableValues((prev) => ({
                                  ...prev,
                                  [variable]: e.target.value,
                                }))
                              }
                              placeholder={`Value for ${variable}`}
                              className="h-8 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-muted-foreground mt-2">
                        These values will replace {"{{variable}}"} placeholders in the
                        assistant's prompts
                      </p>
                    </div>
                  )}
                </div>

                {selectedAssistantId && !isVideoCall && (
                  <div className="space-y-2">
                    <Label>Backend</Label>
                    <BackendPicker
                      value={selectedCallBackend}
                      onChange={setSelectedCallBackend}
                      disabled={initiatingCall}
                    />
                  </div>
                )}

                {selectedAssistantId && !isVideoCall && selectedCallBackend === "v1" && (
                  <div className="space-y-2">
                    <Label htmlFor="phone-number">Outbound Phone Number</Label>
                    <div className="grid grid-cols-1 gap-2">
                      <select
                        id="phone-number"
                        className="w-full p-2 border border-input rounded-md bg-background"
                        value={selectedPhoneNumberId || ""}
                        onChange={(e) => setSelectedPhoneNumberId(e.target.value || null)}
                        disabled={loadingPhoneNumbers || initiatingCall}
                      >
                        <option value="">Select an outbound number</option>
                        {loadingPhoneNumbers ? (
                          <option disabled>Loading phone numbers...</option>
                        ) : phoneNumbers.length === 0 ? (
                          <option disabled>No outbound phone numbers available</option>
                        ) : (
                          phoneNumbers.map((phoneNumber) => (
                            <option key={phoneNumber.id} value={phoneNumber.id}>
                              {phoneNumber.phone_number}
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div className="text-sm text-muted-foreground mt-2">
                      <p>Select which phone number to use for making this outbound call.</p>
                    </div>

                    {phoneNumbers.length === 0 && (
                      <div className="text-sm text-mid mt-2">
                        <p>
                          No outbound phone numbers are configured. Please add a phone number in
                          the Phone Numbers settings.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (isVideoCall && dialogStep === "link") {
                  // Go back to monitoring options
                  setDialogStep("monitoring");
                  setGeneratedVideoLink("");
                } else if (isVideoCall && dialogStep === "monitoring") {
                  // Go back to assistant selection
                  setDialogStep("assistant");
                } else {
                  onOpenChange(false);
                }
              }}
              disabled={initiatingCall}
            >
              {isVideoCall && (dialogStep === "monitoring" || dialogStep === "link")
                ? "Back"
                : "Cancel"}
            </Button>
            <Button
              onClick={() => {
                if (isVideoCall && dialogStep === "assistant" && selectedAssistantId) {
                  // Move to monitoring options step
                  setDialogStep("monitoring");
                } else if (isVideoCall && dialogStep === "monitoring") {
                  // Generate the link
                  generateVideoCallLink();
                } else if (!isVideoCall || dialogStep === "link") {
                  // Proceed with call or start video call
                  handleCall();
                }
              }}
              variant={selectedAssistantId ? "default" : "primaryOutline"}
              isLoading={initiatingCall || isGeneratingLink}
              loadingText={
                isGeneratingLink
                  ? "Generating Link..."
                  : isVideoCall
                    ? "Initiating Video Call..."
                    : "Initiating Call..."
              }
              disabled={
                !leadToCall ||
                (isVideoCall && dialogStep === "assistant" && !selectedAssistantId) ||
                (!isVideoCall &&
                  selectedAssistantId &&
                  selectedCallBackend === "v1" &&
                  !selectedPhoneNumberId) ||
                isGeneratingLink
              }
            >
              {isVideoCall && dialogStep === "assistant"
                ? "Next"
                : isVideoCall && dialogStep === "monitoring"
                  ? "Generate Link"
                  : isVideoCall && dialogStep === "link" && generatedVideoLink
                    ? "Start Video Call Now"
                    : selectedAssistantId
                      ? isVideoCall
                        ? "Start Video Call"
                        : "Call with AI Assistant"
                      : "Call Manually"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CallErrorDialog
        isOpen={isCallErrorDialogOpen}
        onOpenChange={setIsCallErrorDialogOpen}
        errorMessage={callErrorMessage}
        onGoToProviders={() => navigate("/providers")}
        onRetry={() => setIsCallErrorDialogOpen(false)}
      />
    </>
  );
}
